import { useState } from "react";
import api from "@/lib/api";
import { uploadPdf } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Home, Plus, Loader2, X, Paperclip, ImagePlus, Copy, CheckCircle2, Mail, MessageSquare, Link2, BellOff } from "lucide-react";

const EMPTY = {
  title: "", address: "", city: "", state: "", zip_code: "", property_type: "apartment",
  square_feet: "", bedrooms: "", bathrooms: "", monthly_rent: "", security_deposit: "",
  amenities: "", lease_start_date: "", lease_end_date: "",
  lease_document_path: null, lease_document_name: null, images: [],
  renter_name: "", renter_email: "", renter_phone: "", invite_channel: "email",
};

export const MigrateRentalDialog = ({ reload }) => {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const onImages = (e) => {
    const files = Array.from(e.target.files || []).slice(0, 6);
    files.forEach((file) => {
      const r = new FileReader();
      r.onload = () => setF((p) => ({ ...p, images: [...p.images, r.result] }));
      r.readAsDataURL(file);
    });
  };
  const removeImage = (i) => setF((p) => ({ ...p, images: p.images.filter((_, idx) => idx !== i) }));

  const onLease = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadPdf(file);
      setF((p) => ({ ...p, lease_document_path: res.path, lease_document_name: res.name }));
      toast.success("Lease attached");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not upload the lease"); }
    finally { setUploading(false); }
  };

  const submit = async () => {
    if (!f.address || !f.city || !f.state || !f.zip_code) { toast.error("Enter the full property address"); return; }
    if (!f.bedrooms || !f.bathrooms || !f.monthly_rent) { toast.error("Enter bedrooms, bathrooms and monthly rent"); return; }
    if (!f.lease_start_date || !f.lease_end_date) { toast.error("Enter the lease start and end dates"); return; }
    if ((f.invite_channel === "email" || f.invite_channel === "both") && !f.renter_email) { toast.error("Enter the renter's email to send an email invite"); return; }
    if ((f.invite_channel === "sms" || f.invite_channel === "both") && !f.renter_phone) { toast.error("Enter the renter's phone to send a text invite"); return; }
    setBusy(true);
    try {
      const payload = {
        ...f,
        square_feet: f.square_feet ? Number(f.square_feet) : 0,
        bedrooms: Number(f.bedrooms), bathrooms: Number(f.bathrooms),
        monthly_rent: Number(f.monthly_rent), security_deposit: f.security_deposit ? Number(f.security_deposit) : 0,
        amenities: f.amenities.split(",").map((s) => s.trim()).filter(Boolean),
        origin_url: window.location.origin,
      };
      const { data } = await api.post("/landlord/migrate-rental", payload);
      setResult(data);
      toast.success(f.invite_channel === "none" ? "Rental added — tenant not notified" : "Rental added — invite ready to send");
      reload && reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add the rental"); }
    finally { setBusy(false); }
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(result.invite_link); setCopied(true); setTimeout(() => setCopied(false), 2000); }
    catch { toast.error("Copy failed — select the link manually"); }
  };

  const reset = () => { setF(EMPTY); setResult(null); setCopied(false); };

  const notified = result && (result.emailed || result.texted);

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <button data-testid="migrate-rental-btn" className="border border-forest text-forest px-4 py-2 rounded-lg font-medium hover:bg-forest/5 transition-colors flex items-center gap-2 text-sm">
          <Home className="h-4 w-4" /> Add existing rental
        </button>
      </DialogTrigger>
      <DialogContent className="bg-white rounded-2xl max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading">{result ? (notified ? "Invite your tenant" : "Rental added") : "Add an existing rental"}</DialogTitle></DialogHeader>

        {result ? (
          <div className="space-y-4" data-testid="migrate-result">
            <div className="flex items-start gap-3 bg-forest/[0.05] border border-forest/20 rounded-xl p-4">
              <CheckCircle2 className="h-5 w-5 text-forest mt-0.5 shrink-0" />
              <div className="text-sm text-ink">
                <p className="font-medium">Your rental at {result.property.title} is set up.</p>
                <p className="text-mute mt-0.5">
                  {notified ? (
                    <>
                      {result.emailed && "Email invite sent. "}
                      {result.texted && "Text invite sent. "}
                      Share the link below so your current tenant can create their account and add their household details.
                    </>
                  ) : (
                    <>Your tenant was <b>not</b> notified. Whenever you're ready, share the invite link below so they can create their account — or just keep managing the lease and documents yourself.</>
                  )}
                </p>
              </div>
            </div>
            <div>
              <Label>Invite link</Label>
              <div className="mt-1 flex gap-2">
                <input data-testid="invite-link" readOnly value={result.invite_link} className="flex-1 border border-line rounded-lg px-3 py-2 text-sm bg-bone text-ink" onFocus={(e) => e.target.select()} />
                <button data-testid="copy-invite-link" onClick={copyLink} className="inline-flex items-center gap-1.5 bg-forest text-white px-4 rounded-lg text-sm font-medium hover:bg-forest-hover">
                  {copied ? <CheckCircle2 className="h-4 w-4" /> : <Copy className="h-4 w-4" />}{copied ? "Copied" : "Copy"}
                </button>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={reset} className="text-sm border border-line rounded-lg px-4 py-2 text-ink hover:bg-bone">Add another</button>
              <button data-testid="migrate-done" onClick={() => { setOpen(false); reset(); }} className="text-sm bg-ink text-white rounded-lg px-4 py-2 hover:opacity-90">Done</button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-mute -mt-1">Already renting this unit through another platform? Record it here and invite your current tenant to move over — no disruption to their tenancy.</p>

            <div className="space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-forest">Property</p>
              <div><Label>Nickname (optional)</Label><Input data-testid="m-title" value={f.title} onChange={set("title")} placeholder="e.g. Maple St — Unit 2" className="mt-1 border-line" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Label>Street address</Label><Input data-testid="m-address" value={f.address} onChange={set("address")} className="mt-1 border-line" /></div>
                <div><Label>City</Label><Input data-testid="m-city" value={f.city} onChange={set("city")} className="mt-1 border-line" /></div>
                <div><Label>State</Label><Input data-testid="m-state" value={f.state} onChange={set("state")} className="mt-1 border-line" /></div>
                <div><Label>ZIP</Label><Input data-testid="m-zip" value={f.zip_code} onChange={set("zip_code")} className="mt-1 border-line" /></div>
                <div>
                  <Label>Type of housing</Label>
                  <select data-testid="m-type" value={f.property_type} onChange={set("property_type")} className="mt-1 w-full border border-line rounded-lg px-3 py-2 bg-white text-sm">
                    <option value="apartment">Apartment</option><option value="house">House</option><option value="condo">Condo</option><option value="townhouse">Townhouse</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div><Label>Sq ft</Label><Input data-testid="m-sqft" type="number" value={f.square_feet} onChange={set("square_feet")} className="mt-1 border-line" /></div>
                <div><Label>Beds</Label><Input data-testid="m-beds" type="number" value={f.bedrooms} onChange={set("bedrooms")} className="mt-1 border-line" /></div>
                <div><Label>Baths</Label><Input data-testid="m-baths" type="number" value={f.bathrooms} onChange={set("bathrooms")} className="mt-1 border-line" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Monthly rent (upcoming)</Label><Input data-testid="m-rent" type="number" value={f.monthly_rent} onChange={set("monthly_rent")} className="mt-1 border-line" /></div>
                <div><Label>Security deposit (already collected)</Label><Input data-testid="m-deposit" type="number" value={f.security_deposit} onChange={set("security_deposit")} className="mt-1 border-line" /></div>
              </div>
              <div><Label>Amenities / upgrades (comma-separated)</Label><Input data-testid="m-amenities" value={f.amenities} onChange={set("amenities")} placeholder="in-unit laundry, new HVAC, parking" className="mt-1 border-line" /></div>
              <div>
                <Label>Photo (optional)</Label>
                <label className="mt-1 flex items-center gap-2 text-sm text-ink cursor-pointer border border-line border-dashed rounded-lg px-3 py-2 hover:bg-bone w-fit">
                  <ImagePlus className="h-4 w-4 text-forest" /> Add photos
                  <input data-testid="m-images" type="file" accept="image/*" multiple className="hidden" onChange={onImages} />
                </label>
                {f.images.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {f.images.map((src, i) => (
                      <div key={src.slice(0, 40)} className="relative w-16 h-16 rounded-lg overflow-hidden border border-line">
                        <img src={src} alt="" className="w-full h-full object-cover" />
                        <button onClick={() => removeImage(i)} className="absolute top-0.5 right-0.5 bg-white/90 rounded-full p-0.5"><X className="h-3 w-3" /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-3 border-t border-line pt-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-forest">Current lease</p>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Lease start</Label><Input data-testid="m-lease-start" type="date" value={f.lease_start_date} onChange={set("lease_start_date")} className="mt-1 border-line" /></div>
                <div><Label>Lease end</Label><Input data-testid="m-lease-end" type="date" value={f.lease_end_date} onChange={set("lease_end_date")} className="mt-1 border-line" /></div>
              </div>
              <div>
                <Label>Lease agreement (optional PDF)</Label>
                <label className="mt-1 flex items-center gap-2 text-sm text-ink cursor-pointer border border-line border-dashed rounded-lg px-3 py-2 hover:bg-bone w-fit">
                  <Paperclip className="h-4 w-4 text-forest" /> {uploading ? "Uploading…" : f.lease_document_name ? "Change file" : "Upload lease PDF"}
                  <input data-testid="m-lease-doc" type="file" accept="application/pdf" className="hidden" onChange={(e) => onLease(e.target.files?.[0])} />
                </label>
                {f.lease_document_name && <span className="ml-2 text-xs text-mute">{f.lease_document_name}</span>}
              </div>
            </div>

            <div className="space-y-3 border-t border-line pt-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-forest">Invite your current tenant <span className="normal-case font-normal text-mute">(optional)</span></p>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Label>Tenant name</Label><Input data-testid="m-renter-name" value={f.renter_name} onChange={set("renter_name")} className="mt-1 border-line" /></div>
                <div><Label>Tenant email</Label><Input data-testid="m-renter-email" type="email" value={f.renter_email} onChange={set("renter_email")} className="mt-1 border-line" /></div>
                <div><Label>Tenant phone</Label><Input data-testid="m-renter-phone" value={f.renter_phone} onChange={set("renter_phone")} className="mt-1 border-line" /></div>
              </div>
              <div>
                <Label>Send invite by</Label>
                <div className="mt-1 flex flex-wrap gap-2" data-testid="m-invite-channel">
                  {[["email", "Email", Mail], ["sms", "Text", MessageSquare], ["both", "Both", Link2], ["none", "Don't notify", BellOff]].map(([v, label, Icon]) => (
                    <button key={v} type="button" data-testid={`m-channel-${v}`} onClick={() => setF((p) => ({ ...p, invite_channel: v }))}
                      className={`inline-flex items-center gap-1.5 text-sm rounded-lg px-3 py-1.5 border ${f.invite_channel === v ? "bg-forest text-white border-forest" : "border-line text-ink hover:bg-bone"}`}>
                      <Icon className="h-3.5 w-3.5" /> {label}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-mute mt-1.5">
                  {f.invite_channel === "none"
                    ? "No email or text will be sent — the tenant won't be notified. We'll still create a copyable invite link you can share later whenever you're ready."
                    : "We'll send a friendly note explaining the switch and generate a copyable invite link either way."}
                </p>
              </div>
            </div>

            <div className="flex justify-end gap-2 border-t border-line pt-4">
              <button onClick={() => setOpen(false)} className="text-sm border border-line rounded-lg px-4 py-2 text-ink hover:bg-bone">Cancel</button>
              <button data-testid="migrate-submit" onClick={submit} disabled={busy || uploading} className="inline-flex items-center gap-2 bg-forest text-white rounded-lg px-5 py-2 text-sm font-medium hover:bg-forest-hover disabled:opacity-60">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add rental & create invite
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
